import React, { useState } from 'react';
import './DispatchedSummary.css';
import { filterWarehouseData } from '../utils/auth';

function OpenOrdersSummary({ data, onWarehouseClick }) {
  const [excludeMIA, setExcludeMIA] = useState(false);

  if (!data) return null;

  // Filter warehouses based on user access
  const filteredWarehouses = filterWarehouseData(data.warehouses, 'warehouse');

  const formatNumber = (num) => {
    return num.toLocaleString('en-US');
  };

  const formatCurrency = (num) => {
    // Divide by 100 to move decimal 2 places (stored as cents), then round to whole number
    const dollars = Math.round(num / 100);
    return '$' + dollars.toLocaleString('en-US');
  };

  const formatWeight = (num) => {
    // Divide by 10000 to move decimal 4 places, then round to whole number
    const weight = Math.round(num / 10000);
    return weight.toLocaleString('en-US') + ' lbs';
  };

  const handleCardClick = (warehouse) => {
    onWarehouseClick(warehouse);
  };

  // Calculate totals from filtered warehouses
  const calculateTotals = (warehouses) => {
    if (!warehouses || warehouses.length === 0) {
      return {
        totalOpenPicks: 0,
        totalLines: 0,
        totalQuantity: 0,
        totalWeight: 0,
        totalDollars: 0,
        avgDaysOpen: 0
      };
    }

    let totalOpenPicks = 0;
    let totalLines = 0;
    let totalQuantity = 0;
    let totalWeight = 0;
    let totalDollars = 0;
    let totalDaysToPickSum = 0;

    warehouses.forEach(warehouse => {
      totalOpenPicks += parseInt(warehouse.open_picks) || 0;
      totalLines += parseInt(warehouse.total_lines) || 0;
      totalQuantity += parseInt(warehouse.total_quantity) || 0;
      totalWeight += parseInt(warehouse.total_weight) || 0;
      totalDollars += parseFloat(warehouse.total_dollars) || 0;
      totalDaysToPickSum += parseInt(warehouse.total_days_to_pick) || 0;
    });

    const avgDaysOpen = totalOpenPicks > 0 ? totalDaysToPickSum / totalOpenPicks : 0;

    return {
      totalOpenPicks,
      totalLines,
      totalQuantity,
      totalWeight,
      totalDollars,
      avgDaysOpen
    };
  };

  // DUB and SEO get their own totals card, separate from the rest
  const SEPARATE_WAREHOUSES = ['DUB', 'SEO'];
  const whseCode = (w) => String(w.warehouse).trim().toUpperCase();
  const isSeparate = (w) => SEPARATE_WAREHOUSES.includes(whseCode(w));
  const mainWarehouses = filteredWarehouses.filter(w => !isSeparate(w));
  const separateWarehouses = filteredWarehouses.filter(isSeparate);
  // Exclude MIA toggle only affects the main totals card, not the warehouse cards
  const mainTotalsWarehouses = excludeMIA ? mainWarehouses.filter(w => whseCode(w) !== 'MIA') : mainWarehouses;
  const hasMIA = mainWarehouses.some(w => whseCode(w) === 'MIA');

  const renderTotalsCard = (title, warehouses, action = null) => {
    const totals = calculateTotals(warehouses);
    return (
      <div className="grand-totals-card">
        <div className="grand-totals-header">
          <span />
          <h4>{title}</h4>
          <div className="grand-totals-action">{action}</div>
        </div>
        <div className="totals-grid">
          <div className="total-item">
            <span className="total-label">Avg Days Open:</span>
            <span className="total-value">{totals.avgDaysOpen?.toFixed(2) || '0.00'}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Open Picks:</span>
            <span className="total-value">{formatNumber(totals.totalOpenPicks)}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Total Lines:</span>
            <span className="total-value">{formatNumber(totals.totalLines)}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Total Quantity:</span>
            <span className="total-value">{formatNumber(totals.totalQuantity)}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Total Weight:</span>
            <span className="total-value">{formatWeight(totals.totalWeight)}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Total Dollars:</span>
            <span className="total-value">{formatCurrency(totals.totalDollars)}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="dispatched-summary">
      <div className="summary-header">
        <h3>Open Orders by Warehouse</h3>
        <p className="instructions">
          Click a warehouse to view order details
        </p>
      </div>

      {/* Grand Totals - everything except DUB/SEO, then DUB/SEO on their own */}
      {mainWarehouses.length > 0 && renderTotalsCard(
        excludeMIA ? 'All Warehouses (excl. DUB, SEO & MIA) - Total' : 'All Warehouses (excl. DUB & SEO) - Total',
        mainTotalsWarehouses,
        hasMIA && (
          <button
            className={`exclude-whse-btn${excludeMIA ? ' active' : ''}`}
            onClick={() => setExcludeMIA(prev => !prev)}
            title={excludeMIA ? 'Click to include MIA again' : 'Click to remove MIA from these totals'}
          >
            {excludeMIA ? '✓ MIA Excluded' : 'Exclude MIA'}
          </button>
        )
      )}
      {separateWarehouses.length > 0 && renderTotalsCard('DUB & SEO - Total', separateWarehouses)}

      <div className="warehouse-grid">
        {filteredWarehouses.sort((a, b) => a.warehouse.localeCompare(b.warehouse)).map((warehouse) => {
          return (
            <div
              key={warehouse.warehouse}
              className="warehouse-card"
              onClick={() => handleCardClick(warehouse.warehouse)}
            >
              <div className="warehouse-header">
                <h3>{warehouse.warehouse}</h3>
                <span className="country-badge">{warehouse.country}</span>
              </div>

              <div className="warehouse-metrics">
                <div className="metric">
                  <span className="metric-label">Avg Days Open</span>
                  <span className="metric-value">{parseFloat(warehouse.avg_days_open)?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Open Picks</span>
                  <span className="metric-value">{formatNumber(parseInt(warehouse.open_picks) || 0)}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Lines</span>
                  <span className="metric-value">{formatNumber(parseInt(warehouse.total_lines) || 0)}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Quantity</span>
                  <span className="metric-value">{formatNumber(parseInt(warehouse.total_quantity) || 0)}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Weight</span>
                  <span className="metric-value">{formatWeight(parseInt(warehouse.total_weight) || 0)}</span>
                </div>
                <div className="metric">
                  <span className="metric-label">Dollars</span>
                  <span className="metric-value">{formatCurrency(parseFloat(warehouse.total_dollars) || 0)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default OpenOrdersSummary;
